/*
this Components includes (label, input and info)
<FieldGroup
	label=""						Label text value
	>
		this Components includes (input, Left Icons and Right Icon)
		the input can extend all extra attributes, (required, placeholder, type ...)
		<InputGroup
			type=""					  Input type (email, text, ...)
			iconLeft={}				SVG React Component
			iconRight={}			SVG React Component
			alert=""					Alert Message
    />

		for Password Inputs
		<InputPassword
			alert=""					Alert Message
		/>
</FieldGroup>
*/

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import TextareaAutosize from 'react-textarea-autosize';
import Button from './Button';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';

// Import Icons
import { ReactComponent as CopyIcon } from '../img/icons/copy.svg';
import { ReactComponent as EyeOnIcon } from '../img/icons/eye-on.svg';
import { ReactComponent as EyeOffIcon } from '../img/icons/eye-off.svg';
import { ReactComponent as PasswordIcon } from '../img/icons/password.svg';
import calenderIcon from '../img/icons/calender.svg';
import cameraIcon from '../img/icons/upload-camera.svg';

export const FieldGroup = (props) => {
  const history = useHistory();
  const resetPage = () => {
    history.push('/reset-password');
    if (props.hideModal) props.hideModal();
  };
  return (
    <FieldGroupStyle {...props}>
      {props.label && (
        <div>
          <span>{props.label}</span>{' '}
          {props.link && <b onClick={resetPage}>Forgot Password?</b>}
        </div>
      )}
      {props.children}
    </FieldGroupStyle>
  );
};
export const InputGroup = React.forwardRef((props, ref) => {
  return (
    <InputGroupStyle alert={props.alert}>
      <InputStyle ref={ref} {...props} />
      {props.iconLeft && <props.iconLeft className='icon-left' />}
      {props.iconRight && <props.iconRight className='icon-right' />}
      {props.alert && <span>{props.alert}</span>}
    </InputGroupStyle>
  );
});
InputGroup.displayName = 'InputGroup';
export const InputCopyContent = (props) => {
  const input = useRef(null);
  const CopyContent = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(input.current.value);
    toast.success('Copied');
  };
  return (
    <InputCopyContentStyle>
      <InputStyle ref={input} {...props} />
      <Button variant='primary' onClick={CopyContent}>
        <CopyIcon icon='true' />
      </Button>
    </InputCopyContentStyle>
  );
};

export const InputPassword = (props) => {
  const [password, setPassword] = useState(true);

  return (
    <InputGroupStyle alert={props.alert}>
      <InputStyle {...props} type={password ? 'password' : 'text'} iconLeft />
      {<PasswordIcon className='icon-left' />}
      {password ? (
        <EyeOnIcon
          className='icon-right'
          onClick={() => setPassword(!password)}
        />
      ) : (
        <EyeOffIcon
          className='icon-right'
          onClick={() => setPassword(!password)}
        />
      )}
      {props.alert && <span>{props.alert}</span>}
    </InputGroupStyle>
  );
};
export const InputChat = (props) => {
  function iconClick() {
    props.iconClicked((curr) => !curr);
  }

  return (
    <InputGroupStyle chat>
      <InputStyle {...props} />
      {props.iconRight && (
        <props.iconRight onClick={iconClick} className='icon-right' />
      )}
    </InputGroupStyle>
  );
};
export const TextareaGroup = (props) => {
  return (
    <InputGroupStyle alert={props.alert}>
      <TextareaStyle {...props} minRows={props.minRows || 4} />
      {props.alert && <span>{props.alert}</span>}
    </InputGroupStyle>
  );
};
export const Row = styled.div`
  display: flex;
  flex-direction: row;
  > * {
    flex: 1;
    min-width: auto !important;
    :not(:last-child) {
      margin-right: 10px;
    }
  }
  @media (max-width: 576px) {
    flex-direction: ${({ noWrrap }) => !noWrrap && 'column'};
    > * {
      margin-bottom: 10px;
      :not(:last-child) {
        margin-right: ${({ noWrrap }) => !noWrrap && '0'};
      }
    }
  }
`;
const TextareaStyle = styled(TextareaAutosize)`
  width: 100%;
  font-family: 'Poppins Regular';
  padding: 10px 15px;
  padding-left: ${({ iconLeft }) => (iconLeft ? 43 : 15)}px;
  padding-right: ${({ iconRight }) => (iconRight ? 43 : 15)}px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  color: ${({ theme }) => theme.color.darkerText};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  ::placeholder {
    color: ${({ theme }) => theme.color.lighterText};
    opacity: 0.4;
    user-select: none;
  }
  :focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.8);
    background-color: ${({ theme }) => theme.color.secondaryBg};
  }
`;
const InputCopyContentStyle = styled.div`
  position: relative;
  width: 100%;

  input {
    padding-right: 50px;
  }
  button {
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);
    padding: 0;
    width: 30px;
    height: 30px;
    min-width: 24px;
    display: flex;
    align-items: center;
    text-align: center;
    svg {
      margin: 0 auto !important;
    }
  }
`;
// NOTE: This must NOT be a <label>.
// A <label> wrapping multiple interactive elements (checkbox + react-select, etc.)
// can cause browser-default "click anywhere toggles the first input" behavior.
// Use a <div> container and keep the visual styling the same.
const FieldGroupStyle = styled.div`
  display: block;
  max-width: 100%;
  min-width: 250px;
  margin-bottom: 15px;
  position: relative;
  & > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 3px;

    span {
      margin-bottom: 5px;
    }
    b {
      font-size: 12px;
      color: ${({ theme }) => theme.color.primary};
      cursor: pointer;

      :hover {
        color: ${({ theme }) => theme.color.primaryText};
        filter: brightness(1.5);
      }
    }
  }
  @media (max-width: 576px) {
    max-width: 100%;
  }
`;
const InputGroupStyle = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    height: clamp(15px, 15px, 15px);
    width: clamp(15px, 15px, 15px);
    top: 13px;
    opacity: 0.4;
    path {
      fill: ${({ theme }) => theme.color.lighterText};
    }
    &.icon-left {
      left: 15px;
    }
    &.icon-right {
      right: 15px;
      cursor: pointer;
      :hover {
        opacity: 1;
      }
    }
  }
  & > span:last-child {
    display: none;
    margin-top: 5px;
    font-size: 12px;
    letter-spacing: 0.4px;
    font-family: 'Poppins Regular';
  }
  ${({ alert }) =>
    alert &&
    `
      &>input , >textarea{
        transition: box-shadow 200ms ease, background 200ms ease;
        box-shadow: 0 0 0 0.13rem rgba(238, 76, 80, 0.8);
        background: rgba(238, 76, 80, 0.05);
      }
      &>span:last-child{
        display: block;
        color: rgb(238, 76, 80);
      }
  `}
`;
const InputStyle = styled.input`
  position: relative;
  width: 100%;
  padding: 10px 15px;
  padding-left: ${({ iconLeft }) => (iconLeft ? 43 : 15)}px;
  padding-right: ${({ iconRight }) => (iconRight ? 43 : 15)}px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  color: ${({ theme }) => theme.color.darkerText};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  ::placeholder {
    color: ${({ theme }) => theme.color.lighterText};
    opacity: 0.4;
    user-select: none;
  }
  :focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.8);
    background-color: ${({ theme }) => theme.color.secondaryBg};
    & ~ svg {
      opacity: 1;
      path {
        fill: ${({ theme }) => theme.color.darkerText};
      }
    }
  }
  &[type='number'] {
    -moz-appearance: textfield;

    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
  &[type='date'] {
    ::placeholder {
      color: red;
    }
    ::-webkit-calendar-picker-indicator {
      cursor: pointer;
      position: absolute;
      background: none;
      inset: 0;
      left: 90px;
      width: 100%;
      height: 100%;
      z-index: 1;
    }
    ::after {
      content: url(${calenderIcon});
      width: 20px;
      height: 20px;
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  &[type='file'] {
    cursor: pointer;
    ::-webkit-file-upload-button {
      display: none;
    }
    ::after {
      content: '';
      width: 15px;
      height: 15px;
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      background: url(${cameraIcon}) no-repeat center center / contain;
    }
  }
`;
